// Comms channel detection (1.12.212-beta): what the app knows about a phone number's channels, in plain words.
//
// Shared by the browser (composer, badges) and the Edge Functions (phone-line-lookup, twilio-status-callback,
// twilio-inbound-webhook), so the rules live in one place. Rows come from public.phone_channel_capabilities (migration 281).
//
// Texts: Twilio Lookup "Line Type Intelligence" says what kind of line a number is.
//   mobile                      → texts allowed
//   landline / pager / voicemail / premium / shared-cost / UAN → SMS shown disabled with the reason
//   VoIP / toll-free / personal / unknown / never checked      → allowed, with a small "may not receive texts" note
// WhatsApp: learned. 'on' (they messaged us, or a send was delivered/read), 'not_on' (a send failed with 63003), 'unknown'.

export const LOOKUP_RECHECK_DAYS = 365;
export const WHATSAPP_NOT_ON_ERROR = '63003';

const NO_SMS_TYPES = {
    landline: 'This number is a landline — texts won\'t arrive.',
    pager: 'This number is a pager — texts won\'t arrive.',
    voicemail: 'This number only takes voicemail — texts won\'t arrive.',
    premium: 'This is a premium-rate number — texts won\'t arrive.',
    sharedCost: 'This is a shared-cost number — texts won\'t arrive.',
    uan: 'This is a business switchboard number — texts won\'t arrive.',
};
const MAYBE_SMS_TYPES = {
    fixedVoip: 'Internet (VoIP) number — may not receive texts.',
    nonFixedVoip: 'Internet (VoIP) number — may not receive texts.',
    tollFree: 'Toll-free number — may not receive texts.',
    personal: 'Personal (forwarding) number — may not receive texts.',
    unknown: 'Line type unknown — may not receive texts.',
};
const LINE_TYPE_BADGES = {
    mobile: 'Mobile',
    landline: 'Landline',
    fixedVoip: 'VoIP',
    nonFixedVoip: 'VoIP',
    tollFree: 'Toll-free',
    personal: 'Personal number',
    pager: 'Pager',
    voicemail: 'Voicemail only',
    premium: 'Premium',
    sharedCost: 'Shared-cost',
    uan: 'Switchboard',
};
export const KNOWN_LINE_TYPES = Object.keys(LINE_TYPE_BADGES).concat('unknown');

// Review fix round 1: after a failed check, an automatic check waits a day (it used to retry on every page load), and
// "Check now" waits a minute after any check. The server enforces both (phone_channel_lookup_begin, migration 281).
export const LOOKUP_ERROR_BACKOFF_HOURS = 24;
export const LOOKUP_FORCE_COOLDOWN_SECONDS = 60;

/**
 * Should an automatic check run? True when the number has never been checked, or the last check is older than 12 months —
 * and no check failed in the last 24 hours.
 */
export function lookupIsDue(capability, now = new Date()) {
    const erroredAt = capability?.line_type_error_at ? Date.parse(capability.line_type_error_at) : NaN;
    if (Number.isFinite(erroredAt) && now.getTime() - erroredAt < LOOKUP_ERROR_BACKOFF_HOURS * 60 * 60 * 1000) return false;
    const checkedAt = capability?.line_type_checked_at ? Date.parse(capability.line_type_checked_at) : NaN;
    if (!Number.isFinite(checkedAt)) return true;
    return now.getTime() - checkedAt >= LOOKUP_RECHECK_DAYS * 24 * 60 * 60 * 1000;
}

/** When the WhatsApp event in a status callback happened: the send's own time from our log, else now. */
export function whatsappEventTime(logEntry, now = new Date()) {
    for (const value of [logEntry?.sent_at, logEntry?.at, logEntry?.created_at]) {
        const parsed = typeof value === 'string' ? Date.parse(value) : NaN;
        if (Number.isFinite(parsed)) return new Date(Math.min(parsed, now.getTime())).toISOString();
    }
    return now.toISOString();
}

/**
 * Can this number take a text? { state: 'ok' | 'warn' | 'blocked' | 'unchecked', reason }
 * A failed lookup (or none yet) never blocks: it reads as 'unchecked' (allowed, no note).
 */
export function smsCapability(capability) {
    const type = capability?.line_type || '';
    if (!type) return { state: 'unchecked', reason: '' };
    if (type === 'mobile') return { state: 'ok', reason: '' };
    if (NO_SMS_TYPES[type]) return { state: 'blocked', reason: NO_SMS_TYPES[type] };
    return { state: 'warn', reason: MAYBE_SMS_TYPES[type] || MAYBE_SMS_TYPES.unknown };
}

/** Is this number on WhatsApp? { state: 'on' | 'not_on' | 'unknown', reason } — only 'not_on' disables the WhatsApp choice. */
export function whatsappCapability(capability) {
    const status = capability?.whatsapp_status;
    if (status === 'on') return { state: 'on', reason: '' };
    if (status === 'not_on') {
        return {
            state: 'not_on',
            reason: 'Not on WhatsApp — the last WhatsApp message to this number failed (Twilio 63003). Use "Try WhatsApp again" if they have joined since.',
        };
    }
    return { state: 'unknown', reason: '' };
}

/** The small badges for the contact card / thread header, e.g. ["Mobile", "On WhatsApp"]. */
export function channelBadges(capability) {
    const badges = [];
    const type = capability?.line_type;
    if (type && LINE_TYPE_BADGES[type]) {
        const sms = smsCapability(capability);
        badges.push({ key: 'line', label: LINE_TYPE_BADGES[type], tone: sms.state === 'blocked' ? 'red' : sms.state === 'warn' ? 'orange' : 'green', title: sms.reason || `${LINE_TYPE_BADGES[type]} number${capability.carrier_name ? ` · ${capability.carrier_name}` : ''}` });
    }
    const wa = whatsappCapability(capability);
    if (wa.state === 'on') badges.push({ key: 'whatsapp', label: 'On WhatsApp', tone: 'green', title: 'This number has used WhatsApp with us.' });
    if (wa.state === 'not_on') badges.push({ key: 'whatsapp', label: 'Not on WhatsApp', tone: 'red', title: wa.reason });
    return badges;
}

/** What a Twilio status callback teaches about WhatsApp, or null. */
export function whatsappSignalFromStatus({ status, errorCode } = {}) {
    const value = String(status || '').toLowerCase();
    if (value === 'delivered') return { status: 'on', source: 'delivered' };
    if (value === 'read') return { status: 'on', source: 'read' };
    if ((value === 'failed' || value === 'undelivered') && String(errorCode || '') === WHATSAPP_NOT_ON_ERROR) {
        return { status: 'not_on', source: 'failed', errorCode: WHATSAPP_NOT_ON_ERROR };
    }
    return null;
}

/** The Twilio Lookup v2 request for one number (Line Type Intelligence only). */
export function buildLineTypeLookupRequest({ accountSid, authToken, phone }) {
    const credentials = `${accountSid}:${authToken}`;
    const encoded = typeof btoa === 'function' ? btoa(credentials) : Buffer.from(credentials).toString('base64');
    return {
        url: `https://lookups.twilio.com/v2/PhoneNumbers/${encodeURIComponent(phone)}?Fields=line_type_intelligence`,
        method: 'GET',
        headers: { Authorization: `Basic ${encoded}` },
    };
}

/** { lineType, carrier } from a Lookup v2 answer; lineType 'unknown' when Twilio gave nothing usable. */
export function parseLineTypeLookup(body) {
    const intel = body?.line_type_intelligence || {};
    const raw = typeof intel.type === 'string' ? intel.type : '';
    const lineType = KNOWN_LINE_TYPES.includes(raw) ? raw : 'unknown';
    const carrier = typeof intel.carrier_name === 'string' && intel.carrier_name.trim() ? intel.carrier_name.trim().slice(0, 200) : null;
    return { lineType, carrier };
}
